import { Avatar, Box, ListItem, ListItemAvatar, ListItemText, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { deleteActivityComment } from '../../api/activities-nim';
import { CommentDTO, CommentLegacyType } from '../../api/dto';
import { useDeviceType } from '../../hooks/useDeviceType';
import { stringAvatar } from '../../lib/avatar';
import { formatDateTime } from '../../lib/date';
import { translateTask, translateTaskType } from '../../lib/language';
import { sanitizeRichText } from '../../lib/sanitizeHtml';
import { useApplicationContext } from '../application/ApplicationContextProvider';
import { useConfirmationDialog } from '../ConfirmationDialogContext';
import { DeleteCommentButton } from './DeleteCommentButton';

const LEGACY_LABEL: Record<CommentLegacyType, MessageDescriptor['id']> = {
    [CommentLegacyType.DIMR_COMMENT]: 'document.comment.legacyType.dimr',
    [CommentLegacyType.DIMR_FEEDBACK]: 'document.comment.legacyType.dimrFeedback',
    [CommentLegacyType.DIMR_CLOSURE]: 'document.comment.legacyType.dimrClosure',
    [CommentLegacyType.VIC_COMMENT]: 'document.comment.legacyType.vic',
    [CommentLegacyType.INFOLINE_COMMENT]: 'document.comment.legacyType.infoline',
};

const RichTextBox = styled(Box)(() => ({
    '& p': {
        margin: 0,
    },
}));

type CommentListItemProps = Parameters<typeof ListItem>[0] & {
    comment: CommentDTO;
    reloadComments: () => void;
};

export const CommentListItem: React.FC<CommentListItemProps> = ({ comment, reloadComments, ...rest }) => {
    const intl = useIntl();
    const isMobile: boolean = useDeviceType().isMobile;
    const { currentUser } = useApplicationContext();
    const isCreator = currentUser.id === comment.createdBy.id;
    const commentContext = useMemo(
        () =>
            comment.task
                ? intl.formatMessage(
                      { id: 'document.comment.task' },
                      { task: translateTaskType(comment.task.type, intl.locale).typeName }
                  )
                : comment.legacyType !== null
                ? intl.formatMessage({ id: LEGACY_LABEL[comment.legacyType] })
                : null,
        [intl, comment]
    );
    const { isLoading: isDeleting, mutate: deleteComment } = useMutation(deleteActivityComment, {
        onSuccess: async () => reloadComments(),
        onError: () => toast.error(intl.formatMessage({ id: 'common.error' })),
    });
    const deleteConfirmationDialog = useConfirmationDialog();
    const deleteCommentWithConfirmation = (...params: Parameters<typeof deleteComment>) => {
        deleteConfirmationDialog({
            prompt: intl.formatMessage({ id: 'document.comment.deleteConfirm' }),
            onConfirm: () => deleteComment(...params),
        });
    };

    return (
        <ListItem {...rest} sx={{ p: 0, opacity: isDeleting ? 0.5 : undefined, ...rest.sx }}>
            <ListItemAvatar>
                <Avatar {...stringAvatar(comment.createdBy)} />
            </ListItemAvatar>
            <ListItemText
                primary={
                    <Stack direction={{ sm: 'column', md: 'row' }} justifyContent="space-between">
                        <Stack>
                            <Stack
                                direction={{ xs: 'row', md: 'column' }}
                                justifyContent="space-between"
                                alignItems="flex-start"
                            >
                                <Typography>{comment.createdBy.searchLabel}</Typography>
                                {isMobile && isCreator && (
                                    <DeleteCommentButton
                                        commentId={comment.id}
                                        deleteComment={deleteCommentWithConfirmation}
                                        disabled={isDeleting}
                                    />
                                )}
                            </Stack>
                            {!!comment.task && (
                                <Typography variant="caption">
                                    {translateTask(comment.task, intl.locale).assignmentLabel}
                                </Typography>
                            )}
                        </Stack>
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <Typography variant="caption">
                                {commentContext !== null ? commentContext + ' · ' : null}
                                {intl.formatMessage(
                                    { id: 'document.comment.postedOn' },
                                    { date: formatDateTime(comment.createdDate, intl) }
                                )}
                            </Typography>
                        </Stack>
                    </Stack>
                }
                secondary={
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                        {comment.richText ? (
                            <RichTextBox
                                component="span"
                                dangerouslySetInnerHTML={{ __html: sanitizeRichText(comment.text) }}
                            />
                        ) : (
                            <Box
                                sx={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                                component="span"
                                style={{ whiteSpace: 'pre-line' }}
                            >
                                {comment.text.trim()}
                            </Box>
                        )}
                        {!isMobile && isCreator && (
                            <DeleteCommentButton
                                commentId={comment.id}
                                deleteComment={deleteCommentWithConfirmation}
                                disabled={isDeleting}
                            />
                        )}
                    </Stack>
                }
                sx={{ my: 0 }}
            />
        </ListItem>
    );
};
